import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './PianoAdozionePage.api';
import {
  StatoAttivita, TipoFeedback, Piano, PianoDettaglio, Obiettivo, Attivita, Feedback,
  STATO_ATTIVITA_LABEL, TIPO_FEEDBACK_LABEL, STATO_PIANO_LABEL,
  fmtData, fmtDataOra, oggiISO, classeProgress,
} from './adozione';

interface UtenteOpzione { id: number; nome: string; email: string }

export default function PianoAdozionePage() {
  const { can, isSuperadmin, utente } = usePermesso();
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const canEdit = can('adozione.piano', 'edit');

  const [piani, setPiani] = useState<Piano[]>([]);
  const [piano, setPiano] = useState<PianoDettaglio | null>(null);
  const [pianoIdSelezionato, setPianoIdSelezionato] = useState<number | null>(null);
  const [utenti, setUtenti] = useState<UtenteOpzione[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [avvisi, setAvvisi] = useState<string[]>([]);
  const [aperti, setAperti] = useState<Set<number>>(new Set());

  // ── Caricamento ────────────────────────────────────────────────────────────

  const caricaPiani = useCallback(async () => {
    if (!pronta) return;
    setIsLoading(true);
    setErrore('');
    try {
      const qs = societaId ? `?societaId=${societaId}` : '';
      // @api GET /api/adozione/piani?societaId=1 -> {"success":true,"data":[{"id":1,"societaId":1,"titolo":"Adozione BOP 2026","dataInizio":"2026-09-10","dataFine":"2026-12-10","stato":"ATTIVO","giorniPreavviso":3,"societa":{"id":1,"nome":"Pianinvest"},"createdBy":{"id":1,"nome":"Paolo","email":"..."},"_count":{"obiettivi":4}}]}
      const res = await apiFetch<{ data: Piano[] }>(`/api/adozione/piani${qs}`);
      setPiani(res.data);

      // Si apre sul piano attivo: è quello su cui si sta lavorando adesso.
      const attivo = res.data.find(p => p.stato === 'ATTIVO') ?? res.data[0] ?? null;
      setPianoIdSelezionato(prev =>
        prev && res.data.some(p => p.id === prev) ? prev : (attivo ? attivo.id : null));
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [societaId, pronta]);

  const caricaPiano = useCallback(async (id: number) => {
    try {
      // @api GET /api/adozione/piani/1 -> {"success":true,"data":{"id":1,"titolo":"Adozione BOP 2026","descrizione":null,"dataInizio":"2026-09-10","dataFine":"2026-12-10","stato":"ATTIVO","giorniPreavviso":3,"societa":{"id":1,"nome":"Pianinvest"},"createdBy":{"id":1,"nome":"Paolo","email":"..."},"obiettivi":[{"id":1,"titolo":"Ciclo attivo operativo","descrizione":null,"scadenza":"2026-10-31","ordine":0,"attivo":true,"attivita":[{"id":1,"titolo":"Inserire i primi 10 ordini","assegnatoAId":3,"scadenza":"2026-10-15","stato":"DA_FARE","notaAssegnatario":null,"completatoAt":null,"inRitardo":false,"assegnatoA":{"id":3,"nome":"Marco","email":"..."}}],"avanzamento":{"totale":1,"completate":0,"nonApplicabili":0,"inCorso":0,"percentuale":0,"stato":"DA_INIZIARE","inRitardo":false}}],"feedback":[],"riepilogo":{"totale":1,"completate":0,"nonApplicabili":0,"inCorso":0,"percentuale":0,"stato":"DA_INIZIARE","inRitardo":false,"obiettivi":1,"obiettiviInRitardo":0,"feedbackAperti":0}}}
      const res = await apiFetch<{ data: PianoDettaglio }>(`/api/adozione/piani/${id}`);
      setPiano(res.data);
    } catch (e) {
      setErrore((e as Error).message);
      setPiano(null);
    }
  }, []);

  useEffect(() => { caricaPiani(); }, [caricaPiani]);

  useEffect(() => {
    if (pianoIdSelezionato) caricaPiano(pianoIdSelezionato);
    else setPiano(null);
  }, [pianoIdSelezionato, caricaPiano]);

  useEffect(() => {
    if (!canEdit || !pianoIdSelezionato) return;
    // @api GET /api/adozione/piani/1/utenti-assegnabili -> {"success":true,"data":[{"id":3,"nome":"Marco","email":"marco@..."}]}
    apiFetch<{ data: UtenteOpzione[] }>(`/api/adozione/piani/${pianoIdSelezionato}/utenti-assegnabili`)
      .then(r => setUtenti(r.data ?? []))
      .catch(() => setUtenti([]));
  }, [canEdit, pianoIdSelezionato]);

  // ── Azioni ─────────────────────────────────────────────────────────────────

  const ricarica = useCallback(async () => {
    if (pianoIdSelezionato) await caricaPiano(pianoIdSelezionato);
  }, [pianoIdSelezionato, caricaPiano]);

  const esegui = useCallback(async (fn: () => Promise<unknown>) => {
    setErrore('');
    setAvvisi([]);
    try {
      const res = await fn() as { data?: { avvisi?: string[] } };
      if (res?.data?.avvisi?.length) setAvvisi(res.data.avvisi);
      await ricarica();
      return true;
    } catch (e) {
      setErrore((e as Error).message);
      return false;
    }
  }, [ricarica]);

  const toggleObiettivo = (id: number) => {
    setAperti(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const riepilogo = piano?.riepilogo;

  const titoloSocieta = useMemo(() => {
    if (piano) return piano.societa.nome;
    const s = listaSocieta.find(x => String(x.id) === String(societaId));
    return s ? s.nome : '';
  }, [piano, listaSocieta, societaId]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title="Piano di adozione">
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Piano di adozione</h1>
          <p className="bop-page-subtitle">
            {titoloSocieta
              ? `Rollout di BOP in ${titoloSocieta}: obiettivi, attività assegnate e feedback delle persone.`
              : 'Rollout di BOP società per società: obiettivi, attività assegnate e feedback delle persone.'}
          </p>
        </div>
        {canEdit && isSuperadmin && societaId && (
          <div className="bop-flex">
            <NuovoPianoBottone societaId={societaId} onCreato={caricaPiani} />
          </div>
        )}
      </div>

      {!isUnica && (
        <div className="bop-filter-bar">
          <select className="bop-select" value={societaId} onChange={(e) => setSocietaId(e.target.value)}>
            <option value="">Tutte le società</option>
            {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>

          {piani.length > 1 && (
            <select
              className="bop-select"
              value={pianoIdSelezionato ?? ''}
              onChange={(e) => setPianoIdSelezionato(e.target.value ? Number(e.target.value) : null)}
            >
              {piani.map(p => (
                <option key={p.id} value={p.id}>
                  {p.societa.nome} — {p.titolo} ({STATO_PIANO_LABEL[p.stato].label})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {errore && <p className="bop-text-danger">{errore}</p>}
      {avvisi.length > 0 && (
        <ul className="bop-adozione__avvisi">
          {avvisi.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      )}

      {isLoading && <p className="bop-text-muted">Caricamento…</p>}

      {!isLoading && !pronta && (
        <div className="bop-empty-state">
          <p className="bop-empty-state__text">Seleziona una società per vedere il suo piano di adozione.</p>
        </div>
      )}

      {!isLoading && pronta && !piano && (
        <div className="bop-empty-state">
          <p className="bop-empty-state__title">Nessun piano di adozione</p>
          <p className="bop-empty-state__text">
            {canEdit && isSuperadmin && societaId
              ? 'Il rollout per questa società non è ancora partito. Crea un piano per definire obiettivi e assegnare le attività.'
              : 'Il rollout per questa società non è ancora partito.'}
          </p>
        </div>
      )}

      {piano && riepilogo && (
        <>
          <div className="bop-card">
            <div className="bop-card__header">
              <div>
                <h3 className="bop-card__title">{piano.titolo}</h3>
                <div className="bop-adozione__obiettivo-meta">
                  <span>{piano.societa.nome}</span>
                  <span>·</span>
                  <span>dal {fmtData(piano.dataInizio)} al {fmtData(piano.dataFine)}</span>
                  <span className={`bop-badge bop-badge--${STATO_PIANO_LABEL[piano.stato].variant}`}>
                    {STATO_PIANO_LABEL[piano.stato].label}
                  </span>
                </div>
              </div>
              {canEdit && isSuperadmin && (
                <CambioStatoPiano piano={piano} onFatto={async () => { await caricaPiani(); await ricarica(); }} />
              )}
            </div>
            <div className="bop-card__body">
              {piano.descrizione && <p className="bop-text-muted">{piano.descrizione}</p>}
              <div className="bop-adozione__stats">
                <div className="bop-adozione__stat">
                  <div className="bop-adozione__stat-label">Attività completate</div>
                  <div className="bop-adozione__stat-value">
                    {riepilogo.completate + riepilogo.nonApplicabili} / {riepilogo.totale}
                  </div>
                  <div className="bop-adozione__stat-sub">
                    {riepilogo.percentuale}%
                    {riepilogo.nonApplicabili > 0 && ` · ${riepilogo.nonApplicabili} non applicabili`}
                  </div>
                </div>
                <div className={`bop-adozione__stat${riepilogo.obiettiviInRitardo > 0 ? ' bop-adozione__stat--allarme' : ''}`}>
                  <div className="bop-adozione__stat-label">Obiettivi in ritardo</div>
                  <div className="bop-adozione__stat-value">{riepilogo.obiettiviInRitardo}</div>
                  <div className="bop-adozione__stat-sub">su {riepilogo.obiettivi} obiettivi</div>
                </div>
                <div className={`bop-adozione__stat${riepilogo.feedbackAperti > 0 ? ' bop-adozione__stat--allarme' : ''}`}>
                  <div className="bop-adozione__stat-label">Feedback aperti</div>
                  <div className="bop-adozione__stat-value">{riepilogo.feedbackAperti}</div>
                  <div className="bop-adozione__stat-sub">su {piano.feedback.length} totali</div>
                </div>
              </div>
            </div>
          </div>

          {canEdit && isSuperadmin && (
            <NuovoObiettivo pianoId={piano.id} onCreato={ricarica} onErrore={setErrore} />
          )}

          <div className="bop-adozione__obiettivi">
            {piano.obiettivi.length === 0 && (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">
                  Il piano non ha ancora obiettivi. Un piano senza obiettivi non dice a nessuno cosa fare.
                </p>
              </div>
            )}

            {piano.obiettivi.map(o => (
              <ObiettivoCard
                key={o.id}
                obiettivo={o}
                aperto={aperti.has(o.id)}
                onToggle={() => toggleObiettivo(o.id)}
                utenti={utenti}
                canEdit={canEdit && isSuperadmin}
                utenteId={utente?.id ?? 0}
                feedback={piano.feedback.filter(f => f.obiettivoId === o.id)}
                esegui={esegui}
              />
            ))}
          </div>

          <FeedbackPiano
            piano={piano}
            utenteId={utente?.id ?? 0}
            isSuperadmin={isSuperadmin}
            esegui={esegui}
          />
        </>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Obiettivo
// ─────────────────────────────────────────────────────────────────────────────

interface ObiettivoCardProps {
  obiettivo: Obiettivo;
  aperto: boolean;
  onToggle: () => void;
  utenti: UtenteOpzione[];
  canEdit: boolean;
  utenteId: number;
  feedback: Feedback[];
  esegui: (fn: () => Promise<unknown>) => Promise<boolean>;
}

function ObiettivoCard({ obiettivo, aperto, onToggle, utenti, canEdit, utenteId, feedback, esegui }: ObiettivoCardProps) {
  const a = obiettivo.avanzamento;

  return (
    <div className="bop-card">
      <button className="bop-adozione__obiettivo-head" onClick={onToggle} aria-expanded={aperto}>
        <div>
          <h3 className="bop-adozione__obiettivo-titolo">{obiettivo.titolo}</h3>
          <div className="bop-adozione__obiettivo-meta">
            <span>{a.completate + a.nonApplicabili} di {a.totale} attività</span>
            {obiettivo.scadenza && (
              <span className={a.inRitardo ? 'bop-adozione__scadenza--ritardo' : ''}>
                · entro il {fmtData(obiettivo.scadenza)}
              </span>
            )}
            {a.inRitardo && <span className="bop-badge bop-badge--warning">In ritardo</span>}
            {!obiettivo.attivo && <span className="bop-badge bop-badge--neutral">Sospeso</span>}
          </div>
        </div>
        <div className="bop-adozione__obiettivo-azioni">
          <div className="bop-adozione__progress-riga">
            <div className="bop-adozione__progress">
              <div className={`bop-adozione__progress-bar ${classeProgress(a)}`} />
            </div>
            <span className="bop-adozione__progress-valore">{a.percentuale}%</span>
          </div>
          <span className="bop-table__chevron">{aperto ? '▾' : '▸'}</span>
        </div>
      </button>

      {aperto && (
        <div className="bop-adozione__obiettivo-body">
          {obiettivo.descrizione && <p className="bop-text-muted">{obiettivo.descrizione}</p>}

          <table className="bop-table">
            <thead>
              <tr>
                <th>Attività</th>
                <th>Assegnata a</th>
                <th>Scadenza</th>
                <th>Stato</th>
                <th>Nota</th>
                {canEdit && <th className="bop-text-right">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {obiettivo.attivita.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 6 : 5}>
                    <div className="bop-empty-state">
                      <p className="bop-empty-state__text">Nessuna attività: l&apos;obiettivo non è ancora scomposto in lavoro.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                obiettivo.attivita.map(att => (
                  <RigaAttivita
                    key={att.id}
                    attivita={att}
                    mia={att.assegnatoAId === utenteId}
                    canEdit={canEdit}
                    esegui={esegui}
                  />
                ))
              )}
            </tbody>
          </table>

          {canEdit && (
            <NuovaAttivita obiettivoId={obiettivo.id} utenti={utenti} esegui={esegui} />
          )}

          {feedback.length > 0 && (
            <div className="bop-adozione__feedback">
              {feedback.map(f => <FeedbackItem key={f.id} feedback={f} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface RigaAttivitaProps {
  attivita: Attivita;
  mia: boolean;
  canEdit: boolean;
  esegui: (fn: () => Promise<unknown>) => Promise<boolean>;
}

function RigaAttivita({ attivita, mia, canEdit, esegui }: RigaAttivitaProps) {
  const stato = STATO_ATTIVITA_LABEL[attivita.stato];
  const modificabile = mia || canEdit;

  const cambiaStato = (nuovo: StatoAttivita) => esegui(() =>
    // @api PUT /api/adozione/attivita/1/stato {"stato":"COMPLETATA","notaAssegnatario":"fatto"} -> {"success":true}
    apiFetch('/api/adozione/attivita/' + attivita.id + '/stato', {
      method: 'PUT',
      body: JSON.stringify({ stato: nuovo }),
    }));

  const elimina = () => {
    if (!window.confirm(`Eliminare l'attività "${attivita.titolo}"?`)) return;
    // @api DELETE /api/adozione/attivita/1 -> {"success":true}
    esegui(() => apiFetch('/api/adozione/attivita/' + attivita.id, { method: 'DELETE' }));
  };

  return (
    <tr className={mia ? 'bop-adozione__riga--mia' : undefined}>
      <td>{attivita.titolo}</td>
      <td>{attivita.assegnatoA.nome}{mia && ' (tu)'}</td>
      <td className={attivita.inRitardo ? 'bop-adozione__scadenza--ritardo' : undefined}>
        {fmtData(attivita.scadenza)}
      </td>
      <td>
        {modificabile ? (
          <select
            className="bop-select-inline--sm"
            value={attivita.stato}
            onChange={(e) => cambiaStato(e.target.value as StatoAttivita)}
          >
            {(Object.keys(STATO_ATTIVITA_LABEL) as StatoAttivita[]).map(s => (
              <option key={s} value={s}>{STATO_ATTIVITA_LABEL[s].label}</option>
            ))}
          </select>
        ) : (
          <span className={`bop-badge bop-badge--${stato.variant}`}>{stato.label}</span>
        )}
      </td>
      <td className="bop-text-muted">{attivita.notaAssegnatario ?? '—'}</td>
      {canEdit && (
        <td className="bop-text-right">
          <BopRowActions actions={[
            { icon: <RowIcon.Trash />, label: 'Elimina', onClick: elimina, variant: 'danger' },
          ]} />
        </td>
      )}
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Form di creazione
// ─────────────────────────────────────────────────────────────────────────────

function NuovoPianoBottone({ societaId, onCreato }: { societaId: string; onCreato: () => void }) {
  const [aperto, setAperto] = useState(false);
  const [titolo, setTitolo] = useState('');
  const [dataInizio, setDataInizio] = useState(oggiISO());
  const [dataFine, setDataFine] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [errore, setErrore] = useState('');
  const [salvando, setSalvando] = useState(false);

  const salva = async () => {
    setSalvando(true);
    setErrore('');
    try {
      // @api POST /api/adozione/piani {"societaId":1,"titolo":"Adozione BOP 2026","dataInizio":"2026-09-10","dataFine":"2026-12-10","descrizione":null,"giorniPreavviso":3} -> {"success":true,"data":{"id":1}}
      await apiFetch('/api/adozione/piani', {
        method: 'POST',
        body: JSON.stringify({
          societaId: Number(societaId), titolo, dataInizio, dataFine,
          descrizione: descrizione || null,
        }),
      });
      setAperto(false);
      setTitolo(''); setDataFine(''); setDescrizione('');
      onCreato();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <button className="bop-btn bop-btn-primary" onClick={() => setAperto(true)}>+ Nuovo piano</button>
      {aperto && (
        <div className="bop-modal-overlay" onClick={() => setAperto(false)}>
          <div className="bop-modal bop-modal--md" onClick={e => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Nuovo piano di adozione</h2>
              <button className="bop-modal__close" onClick={() => setAperto(false)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body">
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="piano-titolo">
                  Titolo <span className="bop-field__required">*</span>
                </label>
                <input id="piano-titolo" className="bop-field__input" value={titolo}
                  onChange={e => setTitolo(e.target.value)} placeholder="Adozione BOP — autunno 2026" />
              </div>
              <div className="bop-form-row">
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="piano-inizio">
                    Dal <span className="bop-field__required">*</span>
                  </label>
                  <input id="piano-inizio" type="date" className="bop-field__input"
                    value={dataInizio} onChange={e => setDataInizio(e.target.value)} />
                </div>
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="piano-fine">
                    Al <span className="bop-field__required">*</span>
                  </label>
                  <input id="piano-fine" type="date" className="bop-field__input"
                    value={dataFine} onChange={e => setDataFine(e.target.value)} />
                </div>
              </div>
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="piano-descr">Descrizione</label>
                <textarea id="piano-descr" className="bop-field__textarea" rows={3}
                  value={descrizione} onChange={e => setDescrizione(e.target.value)} />
              </div>
              {errore && <p className="bop-field__error">{errore}</p>}
              <p className="bop-field__hint">
                Il piano nasce in bozza. Va attivato quando è pronto: solo un piano attivo
                genera le attività in &quot;Le mie attività&quot; e i promemoria.
              </p>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setAperto(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salva}
                disabled={salvando || !titolo || !dataInizio || !dataFine}>
                {salvando ? 'Salvataggio…' : 'Crea piano'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CambioStatoPiano({ piano, onFatto }: { piano: PianoDettaglio; onFatto: () => void }) {
  const [errore, setErrore] = useState('');

  const cambia = async (stato: string) => {
    setErrore('');
    try {
      // @api PUT /api/adozione/piani/1/stato {"stato":"ATTIVO"} -> {"success":true}
      await apiFetch(`/api/adozione/piani/${piano.id}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato }),
      });
      onFatto();
    } catch (e) {
      setErrore((e as Error).message);
    }
  };

  return (
    <div className="bop-flex">
      {errore && <span className="bop-text-danger">{errore}</span>}
      {piano.stato === 'BOZZA' && (
        <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={() => cambia('ATTIVO')}>
          Attiva il piano
        </button>
      )}
      {piano.stato === 'ATTIVO' && (
        <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => cambia('CONCLUSO')}>
          Concludi il piano
        </button>
      )}
      {piano.stato === 'CONCLUSO' && (
        <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => cambia('ATTIVO')}>
          Riapri il piano
        </button>
      )}
    </div>
  );
}

function NuovoObiettivo({ pianoId, onCreato, onErrore }: {
  pianoId: number; onCreato: () => void; onErrore: (m: string) => void;
}) {
  const [titolo, setTitolo] = useState('');
  const [scadenza, setScadenza] = useState('');
  const [salvando, setSalvando] = useState(false);

  const salva = async () => {
    if (!titolo.trim()) return;
    setSalvando(true);
    try {
      // @api POST /api/adozione/piani/1/obiettivi {"titolo":"Ciclo attivo operativo","scadenza":"2026-10-31","descrizione":null} -> {"success":true,"data":{"id":1}}
      await apiFetch(`/api/adozione/piani/${pianoId}/obiettivi`, {
        method: 'POST',
        body: JSON.stringify({ titolo, scadenza: scadenza || null }),
      });
      setTitolo(''); setScadenza('');
      onCreato();
    } catch (e) {
      onErrore((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bop-adozione__form">
      <div className="bop-field">
        <label className="bop-field__label" htmlFor="ob-titolo">Nuovo obiettivo</label>
        <input id="ob-titolo" className="bop-field__input" value={titolo}
          onChange={e => setTitolo(e.target.value)}
          placeholder="Es. Ciclo attivo operativo su BOP" />
      </div>
      <div className="bop-field">
        <label className="bop-field__label" htmlFor="ob-scadenza">Entro il</label>
        <input id="ob-scadenza" type="date" className="bop-field__input"
          value={scadenza} onChange={e => setScadenza(e.target.value)} />
      </div>
      <div className="bop-adozione__form-azioni">
        <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando || !titolo.trim()}>
          {salvando ? 'Aggiunta…' : '+ Aggiungi obiettivo'}
        </button>
      </div>
    </div>
  );
}

function NuovaAttivita({ obiettivoId, utenti, esegui }: {
  obiettivoId: number; utenti: UtenteOpzione[]; esegui: (fn: () => Promise<unknown>) => Promise<boolean>;
}) {
  const [titolo, setTitolo] = useState('');
  const [assegnatoAId, setAssegnatoAId] = useState('');
  const [scadenza, setScadenza] = useState('');
  const [salvando, setSalvando] = useState(false);

  const salva = async () => {
    if (!titolo.trim() || !assegnatoAId) return;
    setSalvando(true);
    // @api POST /api/adozione/obiettivi/1/attivita {"titolo":"Inserire i primi 10 ordini","assegnatoAId":3,"scadenza":"2026-10-15","descrizione":null} -> {"success":true,"data":{"id":1,"avvisi":[]}}
    const ok = await esegui(() => apiFetch(`/api/adozione/obiettivi/${obiettivoId}/attivita`, {
      method: 'POST',
      body: JSON.stringify({ titolo, assegnatoAId: Number(assegnatoAId), scadenza: scadenza || null }),
    }));
    if (ok) { setTitolo(''); setScadenza(''); }
    setSalvando(false);
  };

  return (
    <div className="bop-adozione__form">
      <div className="bop-field">
        <label className="bop-field__label" htmlFor={`att-titolo-${obiettivoId}`}>Nuova attività</label>
        <input id={`att-titolo-${obiettivoId}`} className="bop-field__input" value={titolo}
          onChange={e => setTitolo(e.target.value)} placeholder="Es. Inserire i primi 10 ordini clienti" />
      </div>
      <div className="bop-field">
        <label className="bop-field__label" htmlFor={`att-utente-${obiettivoId}`}>Assegnata a</label>
        <select id={`att-utente-${obiettivoId}`} className="bop-field__select"
          value={assegnatoAId} onChange={e => setAssegnatoAId(e.target.value)}>
          <option value="">Seleziona…</option>
          {utenti.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
      </div>
      <div className="bop-field">
        <label className="bop-field__label" htmlFor={`att-scad-${obiettivoId}`}>Entro il</label>
        <input id={`att-scad-${obiettivoId}`} type="date" className="bop-field__input"
          value={scadenza} onChange={e => setScadenza(e.target.value)} />
      </div>
      <div className="bop-adozione__form-azioni">
        <button className="bop-btn bop-btn-primary" onClick={salva}
          disabled={salvando || !titolo.trim() || !assegnatoAId}>
          {salvando ? 'Aggiunta…' : '+ Aggiungi'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Feedback
// ─────────────────────────────────────────────────────────────────────────────

export function FeedbackItem({ feedback }: { feedback: Feedback }) {
  const tipo = TIPO_FEEDBACK_LABEL[feedback.tipo];
  return (
    <div className="bop-adozione__feedback-item">
      <div className="bop-adozione__feedback-head">
        <span className={`bop-badge bop-badge--${tipo.variant}`}>{tipo.label}</span>
        <span className="bop-adozione__feedback-autore">{feedback.autore.nome}</span>
        <span>· {fmtDataOra(feedback.createdAt)}</span>
        {feedback.attivita && <span>· su &quot;{feedback.attivita.titolo}&quot;</span>}
        {feedback.stato !== 'APERTO' && (
          <span className="bop-badge bop-badge--neutral">{feedback.stato === 'CHIUSO' ? 'Chiuso' : 'Letto'}</span>
        )}
      </div>
      <p className="bop-adozione__feedback-testo">{feedback.testo}</p>
      {feedback.rispostaTesto && (
        <div className="bop-adozione__feedback-risposta">
          <strong>{feedback.rispostaDa?.nome ?? 'Risposta'}:</strong> {feedback.rispostaTesto}
        </div>
      )}
    </div>
  );
}

function FeedbackPiano({ piano, utenteId, isSuperadmin, esegui }: {
  piano: PianoDettaglio;
  utenteId: number;
  isSuperadmin: boolean;
  esegui: (fn: () => Promise<unknown>) => Promise<boolean>;
}) {
  const [testo, setTesto] = useState('');
  const [tipo, setTipo] = useState<TipoFeedback>('OPINIONE');
  const [salvando, setSalvando] = useState(false);
  const [rispostaA, setRispostaA] = useState<number | null>(null);
  const [risposta, setRisposta] = useState('');

  // I feedback agganciati a un obiettivo si leggono dentro quell'obiettivo.
  const generali = piano.feedback.filter(f => !f.obiettivoId);

  const invia = async () => {
    if (!testo.trim()) return;
    setSalvando(true);
    // @api POST /api/adozione/feedback {"pianoId":1,"tipo":"DIFFICOLTA","testo":"..."} -> {"success":true,"data":{"id":1}}
    const ok = await esegui(() => apiFetch('/api/adozione/feedback', {
      method: 'POST',
      body: JSON.stringify({ pianoId: piano.id, tipo, testo }),
    }));
    if (ok) setTesto('');
    setSalvando(false);
  };

  const rispondi = async (id: number) => {
    if (!risposta.trim()) return;
    // @api PUT /api/adozione/feedback/1 {"rispostaTesto":"...","stato":"CHIUSO"} -> {"success":true}
    const ok = await esegui(() => apiFetch(`/api/adozione/feedback/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ rispostaTesto: risposta, stato: 'CHIUSO' }),
    }));
    if (ok) { setRisposta(''); setRispostaA(null); }
  };

  return (
    <div className="bop-card">
      <div className="bop-card__header">
        <h3 className="bop-card__title">Feedback sul piano</h3>
      </div>
      <div className="bop-card__body">
        <p className="bop-text-muted">
          Opinioni, difficoltà e proposte su come sta andando l&apos;adozione. Per un errore
          del sistema conviene invece aprire una richiesta di assistenza.
        </p>

        <div className="bop-adozione__form">
          <div className="bop-field">
            <label className="bop-field__label" htmlFor="fb-tipo">Tipo</label>
            <select id="fb-tipo" className="bop-field__select" value={tipo}
              onChange={e => setTipo(e.target.value as TipoFeedback)}>
              {(Object.keys(TIPO_FEEDBACK_LABEL) as TipoFeedback[]).map(t => (
                <option key={t} value={t}>{TIPO_FEEDBACK_LABEL[t].label}</option>
              ))}
            </select>
          </div>
          <div className="bop-field">
            <label className="bop-field__label" htmlFor="fb-testo">Il tuo feedback</label>
            <input id="fb-testo" className="bop-field__input" value={testo}
              onChange={e => setTesto(e.target.value)}
              placeholder="Cosa hai incontrato usando BOP?" />
          </div>
          <div className="bop-adozione__form-azioni">
            <button className="bop-btn bop-btn-primary" onClick={invia} disabled={salvando || !testo.trim()}>
              {salvando ? 'Invio…' : 'Invia'}
            </button>
          </div>
        </div>

        {generali.length === 0 ? (
          <div className="bop-empty-state">
            <p className="bop-empty-state__text">
              {piano.feedback.length > 0
                // Senza questa distinzione il riepilogo in testa ("Feedback aperti: 1")
                // e questo riquadro vuoto sembrano contraddirsi.
                ? `Nessun feedback generale. ${piano.feedback.length === 1
                    ? 'L\'unico feedback riguarda un obiettivo e si legge aprendolo.'
                    : `I ${piano.feedback.length} feedback riguardano singoli obiettivi e si leggono aprendoli.`}`
                : 'Ancora nessun feedback su questo piano.'}
            </p>
          </div>
        ) : (
          <div className="bop-adozione__feedback">
            {generali.map(f => (
              <div key={f.id}>
                <FeedbackItem feedback={f} />
                {isSuperadmin && f.stato === 'APERTO' && (
                  rispostaA === f.id ? (
                    <div className="bop-adozione__form bop-adozione__form--stretto">
                      <div className="bop-field">
                        <label className="bop-field__label" htmlFor={`risp-${f.id}`}>Risposta</label>
                        <input id={`risp-${f.id}`} className="bop-field__input" value={risposta}
                          onChange={e => setRisposta(e.target.value)} />
                      </div>
                      <div className="bop-adozione__form-azioni">
                        <button className="bop-btn bop-btn-secondary" onClick={() => setRispostaA(null)}>Annulla</button>
                        <button className="bop-btn bop-btn-primary" onClick={() => rispondi(f.id)}>Rispondi e chiudi</button>
                      </div>
                    </div>
                  ) : (
                    <button className="bop-btn bop-btn-link" onClick={() => { setRispostaA(f.id); setRisposta(''); }}>
                      Rispondi
                    </button>
                  )
                )}
                {f.autoreId === utenteId && f.stato === 'APERTO' && !isSuperadmin && (
                  <p className="bop-field__hint">Puoi ancora correggerlo finché non viene preso in carico.</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
